import { useEffect, useMemo, useRef, useState } from 'react';
import { type RootState } from '@/store';
import { useSelector } from 'react-redux';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { calculateClosingDisclosure } from '@/utils/calculator';
import { getCurrentInterestRate } from '@/utils/calculator/rateIntegration';
import {
  estimateRefinanceProduct,
  getRefinanceImpliedEquityDown,
} from '@/utils/refinanceEstimationFromProduct';
import { Product, ProductEsitmation } from '@/types/ProductType';

export const useEstimation = (
  product: Product,
  // Optional: current slider values (takes precedence over saved values)
  currentDownPayment?: number,
  currentLoanProgram?: string,
  /** Bridge program: term in months (6/12/18/24); drives disclosure + payments */
  bridgeTermMonths?: number
) => {
  const rateMatrices = useSelector(
    (state: RootState) => state.interestRate.rateMatrices
  );
  const matricesLoaded = useSelector(
    (state: RootState) => state.interestRate.matricesLoaded
  );

  // Get a stable reference to interestRateState - only select what we need
  const hybridInterestRateState = useSelector(
    (state: RootState) => state.hybridInterestRate
  );

  const [estimation, setEstimation] = useState<ProductEsitmation | null>(null);

  // Use refs to track previous values and prevent infinite loops
  const prevValuesRef = useRef<{
    productId: string;
    downPayment: number;
    loanProgram: string;
    bridgeTermMonths: number | undefined;
    rateMatricesHash: string;
    refiSignature: string;
  } | null>(null);

  // Memoize key values to prevent unnecessary recalculations
  const activeLoanProgram = useMemo(() => {
    if (product.transactionMode === 'refinance') {
      const p =
        currentLoanProgram ||
        product.savedLoanType ||
        product.refinance?.selection?.program ||
        product.user?.loanProgram ||
        'conventional';
      return String(p).toLowerCase();
    }
    return currentLoanProgram || product.user?.loanProgram || 'CONVENTIONAL';
  }, [
    product.transactionMode,
    product.savedLoanType,
    product.refinance?.selection?.program,
    currentLoanProgram,
    product.user?.loanProgram,
  ]);

  const activeDownPayment = useMemo(() => {
    if (product.transactionMode === 'refinance' && product.refinance) {
      if (currentDownPayment !== undefined) return currentDownPayment;
      return getRefinanceImpliedEquityDown(product);
    }
    if (currentDownPayment !== undefined) return currentDownPayment;
    if (
      product.savedDownPayment != null &&
      Number.isFinite(product.savedDownPayment)
    ) {
      return product.savedDownPayment;
    }
    return product.user?.loanInfo?.downPayment || 0;
  }, [
    product,
    product.transactionMode,
    product.refinance,
    product.price,
    product.savedDownPayment,
    currentDownPayment,
    product.user?.loanInfo?.downPayment,
  ]);

  useEffect(() => {
    if (!product) {
      setEstimation(null);
      return;
    }

    const canEstimateRefinanceWithoutUser =
      product.transactionMode === 'refinance' && Boolean(product.refinance);

    if (!product.user && !canEstimateRefinanceWithoutUser) {
      setEstimation(null);
      return;
    }

    // Create a simple hash of rateMatrices to detect actual changes
    const rateMatricesHash = JSON.stringify(
      Object.keys(rateMatrices).map((key) => ({
        key,
        hasMatrix: !!rateMatrices[key as keyof typeof rateMatrices]?.matrix,
      }))
    );

    const refiSignature =
      product.transactionMode === 'refinance' && product.refinance
        ? JSON.stringify(product.refinance)
        : '';

    // Check if values have actually changed to prevent infinite loops
    const currentValues = {
      productId: product._id,
      downPayment: activeDownPayment,
      loanProgram: activeLoanProgram,
      bridgeTermMonths,
      rateMatricesHash,
      refiSignature,
    };

    const prevValues = prevValuesRef.current;
    const hasChanged =
      !prevValues ||
      prevValues.productId !== currentValues.productId ||
      Math.abs(prevValues.downPayment - currentValues.downPayment) > 0.01 ||
      prevValues.loanProgram !== currentValues.loanProgram ||
      prevValues.bridgeTermMonths !== currentValues.bridgeTermMonths ||
      prevValues.rateMatricesHash !== currentValues.rateMatricesHash ||
      prevValues.refiSignature !== currentValues.refiSignature;

    if (!hasChanged) {
      return; // Skip if nothing meaningful changed
    }

    prevValuesRef.current = currentValues;

    // Create a minimal state object for calculators (purchase + refinance)
    const minimalState = {
      interestRate: {
        rateMatrices,
        matricesLoaded,
        activeConfigurationId: null,
        loading: false,
        error: null,
        selectedLoanType: null,
        rateChangeHistory: [],
        draftChanges: [],
        currentAPRCalculation: null,
        aprResult: null,
      },
      hybridInterestRate: hybridInterestRateState,
    } as unknown as RootState;

    const isRefinanceFlow =
      product.transactionMode === 'refinance' && product.refinance;

    if (isRefinanceFlow) {
      const est = estimateRefinanceProduct(product, minimalState, {
        loanProgram: activeLoanProgram,
        downPayment: activeDownPayment,
      });
      setEstimation(est as ProductEsitmation | null);
      return;
    }

    // Get the appropriate base rate for the loan program using the new interest rate system
    const getBaseRate = (
      loanProgram: string,
      creditScore: number,
      ltv: number
    ) => {
      // Map loan program strings to our LoanType enum
      const loanTypeMap: Record<string, string> = {
        CONVENTIONAL: 'Conventional',
        FHA: 'FHA',
        VA: 'VA',
        USDA: 'USDA',
        DSCR: 'DSCR',
        FIX_AND_FLIP: 'FixAndFlip',
        CASH: 'Cash',
        COMMERCIAL: 'Commercial',
        BRIDGE: 'Bridge',
      };

      const programKey = loanProgram.toUpperCase();
      if (programKey === 'CASH') {
        return 0;
      }

      const loanType = loanTypeMap[programKey];

      if (loanType && rateMatrices[loanType as keyof typeof rateMatrices]) {
        // Use the new interest rate system
        // Create a minimal state object for getCurrentInterestRate
        const minimalState = {
          interestRate: {
            rateMatrices,
            activeConfigurationId: null,
            loading: false,
            error: null,
            selectedLoanType: null,
            rateChangeHistory: [],
            draftChanges: [],
            currentAPRCalculation: null,
            aprResult: null,
          },
          hybridInterestRate: hybridInterestRateState,
        } as unknown as RootState;

        return getCurrentInterestRate(
          minimalState,
          loanType as any,
          creditScore,
          ltv
        );
      }

      // Fallback to default rates if no matrix is configured
      const defaultRates: Record<string, number> = {
        CONVENTIONAL: 6.75,
        FHA: 6.5,
        VA: 6.25,
        USDA: 6.0,
        DSCR: 7.5,
        FIX_AND_FLIP: 9.0,
        CASH: 0,
        COMMERCIAL: 8.0,
        BRIDGE: 10.5,
      };

      return defaultRates[programKey] ?? 6.75;
    };

    // Calculate LTV for rate lookup using current slider value
    const purchasePrice = product?.loanInfo?.offerPrice || product.price;
    const loanAmount = purchasePrice - activeDownPayment;
    const ltv = loanAmount / purchasePrice;
    const creditScore = product.user?.loanInfo?.creditScore || 700;

    // Get rate from backend (admin-configured rates)
    const marketInterestRate = getBaseRate(
      activeLoanProgram.toUpperCase(),
      creditScore,
      ltv
    );

    // Log rate usage (disabled to reduce console noise - enable for debugging)
    // Uncomment below to enable logging:
    // console.log('[ProductDetail Estimation] Rate used in calculation (from backend/admin)', {
    //   productId: product._id,
    //   loanProgram: activeLoanProgram,
    //   creditScore,
    //   ltv: (ltv * 100).toFixed(2) + '%',
    //   purchasePrice,
    //   downPayment: activeDownPayment,
    //   loanAmount,
    //   marketInterestRate,
    //   source: 'backend-admin-rates',
    //   usingSliderValue: currentDownPayment !== undefined,
    //   timestamp: new Date().toISOString(),
    //   note: 'Rate from admin configuration, updates with slider changes',
    // });

    const purchaseProgram = String(
      activeLoanProgram
    ).toLowerCase() as USER_LOAN_PROGRAM;

    const bridgeMonths =
      purchaseProgram === USER_LOAN_PROGRAM.BRIDGE
        ? bridgeTermMonths ?? 12
        : undefined;

    const estimation = calculateClosingDisclosure(
      {
        type: purchaseProgram,
        listPrice: product.price,
        offerPrice: product?.loanInfo?.offerPrice,
        hoaFees: product?.loanInfo?.hoaFees,
        commission: product?.loanInfo?.commission,
        zipCode: product?.loanInfo?.zipCode,
        monthlyPropertyTaxes:
          product?.loanInfo?.monthlyPropertyTaxes ?? undefined,
        monthlyHomeownersInsurance:
          product?.loanInfo?.monthlyHomeownersInsurance ?? undefined,
        creditScore: product.user?.loanInfo?.creditScore,
        downPayment: activeDownPayment, // Use current slider value
        loanTerm:
          purchaseProgram === USER_LOAN_PROGRAM.BRIDGE
            ? bridgeMonths
            : product.user?.loanInfo?.loanTerm,
        marketInterestRate: marketInterestRate, // From backend/admin
        rent: product.loanInfo?.rent,
        experience: product.loanInfo?.experience,
        rehab: product.loanInfo?.rehab,
        arv: product.loanInfo?.arv,
        removePrepaymentPenalty:
          product?.loanInfo?.removePrepaymentPenalty || false,
      },
      minimalState
    );

    setEstimation(estimation);
  }, [
    product,
    product._id,
    product.savedDownPayment,
    product.loanInfo?.offerPrice,
    product.transactionMode,
    product.refinance,
    activeDownPayment,
    activeLoanProgram,
    rateMatrices,
    matricesLoaded,
    hybridInterestRateState,
    bridgeTermMonths,
  ]);

  return { estimation };
};
